import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { candidateAPI } from '../services/api';
import { formatAxiosError } from '../utils/apiError';

const PRESCREENING_CATEGORY_LABELS = {
  technical_skills: 'Technical skills',
  experience: 'Experience',
  education: 'Education',
  behavioral: 'Behavioral',
  behavioral_situational: 'Behavioral',
  problem_solving: 'Problem solving',
  communication: 'Communication',
  leadership: 'Leadership',
  certifications: 'Certifications',
  projects: 'Projects',
  general: 'General',
};

function formatPrescreeningCategory(category) {
  if (!category || typeof category !== 'string') {
    return '';
  }
  const key = category.trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (PRESCREENING_CATEGORY_LABELS[key]) {
    return PRESCREENING_CATEGORY_LABELS[key];
  }
  return category
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

const PrescreeningQuestions = ({ onComplete }) => {
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [session, setSession] = useState(null);
  const [showResults, setShowResults] = useState(false);
  
  // New state for conditional enable/disable
  const [prescreeningState, setPrescreeningState] = useState({
    isAvailable: false,        // Questions exist
    isEnabled: false,          // Can be accessed
    isLoading: false,          // Currently loading
    hasResume: false,          // Resume uploaded
    isGeneratingQuestions: false, // Questions being generated
    statusMessage: '',         // Status message to show
    nextAction: ''             // Next action for user
  });

  // Load existing session and questions
  useEffect(() => {
    loadPrescreeningData();
  }, []);

  const loadPrescreeningData = async () => {
    try {
      setIsLoading(true);
      setPrescreeningState(prev => ({ ...prev, isLoading: true }));
      
      const profileResponse = await candidateAPI.getProfile();
      const hasResume = Boolean(
        profileResponse.data?.resumeUrl || profileResponse.data?.resumeExtractedData
      );

      // No session yet is a 404 — that is expected until questions are generated.
      let sessionData = null;
      try {
        const sessionResponse = await candidateAPI.getPrescreeningSession();
        sessionData = sessionResponse.data || null;
      } catch (sessionError) {
        if (sessionError.response?.status !== 404) {
          throw sessionError;
        }
      }
      setSession(sessionData);
      
      if (sessionData) {
        let loadedQuestions = [];
        try {
          const questionsResponse = await candidateAPI.getPrescreeningQuestions();
          loadedQuestions = questionsResponse.data || [];
        } catch (questionsError) {
          if (questionsError.response?.status !== 404) {
            throw questionsError;
          }
        }
        setQuestions(loadedQuestions);
        
        if (sessionData.sessionStatus === 'completed') {
          setShowResults(true);
          setPrescreeningState({
            isAvailable: true,
            isEnabled: false,
            isLoading: false,
            hasResume: true,
            isGeneratingQuestions: false,
            statusMessage: 'Assessment completed - View results',
            nextAction: 'view_results'
          });
        } else if (loadedQuestions.length > 0) {
          setPrescreeningState({
            isAvailable: true,
            isEnabled: true,
            isLoading: false,
            hasResume: true,
            isGeneratingQuestions: false,
            statusMessage: `Continue assessment (${sessionData.answeredQuestions}/${sessionData.totalQuestions} completed)`,
            nextAction: 'continue_assessment'
          });
        } else if (hasResume) {
          setPrescreeningState({
            isAvailable: false,
            isEnabled: true,
            isLoading: false,
            hasResume: true,
            isGeneratingQuestions: false,
            statusMessage: 'Resume uploaded - Generate personalized questions',
            nextAction: 'generate_questions'
          });
        } else {
          setPrescreeningState({
            isAvailable: false,
            isEnabled: false,
            isLoading: false,
            hasResume: false,
            isGeneratingQuestions: false,
            statusMessage: 'Upload your resume to unlock prescreening questions',
            nextAction: 'upload_resume'
          });
        }
      } else if (hasResume) {
        setPrescreeningState({
          isAvailable: false,
          isEnabled: true,
          isLoading: false,
          hasResume: true,
          isGeneratingQuestions: false,
          statusMessage: 'Resume uploaded - Generate personalized questions',
          nextAction: 'generate_questions'
        });
      } else {
        setPrescreeningState({
          isAvailable: false,
          isEnabled: false,
          isLoading: false,
          hasResume: false,
          isGeneratingQuestions: false,
          statusMessage: 'Upload your resume to unlock prescreening questions',
          nextAction: 'upload_resume'
        });
      }
    } catch (error) {
      console.error('Error loading prescreening data:', error);
      
      setPrescreeningState({
        isAvailable: false,
        isEnabled: false,
        isLoading: false,
        hasResume: false,
        isGeneratingQuestions: false,
        statusMessage: formatAxiosError(error, 'Failed to load prescreening data'),
        nextAction: 'retry'
      });
      
      toast.error(formatAxiosError(error, 'Failed to load prescreening data'));
    } finally {
      setIsLoading(false);
    }
  };

  const generateQuestions = async () => {
    try {
      setIsGenerating(true);
      setPrescreeningState(prev => ({ 
        ...prev, 
        isGeneratingQuestions: true,
        statusMessage: 'Generating personalized questions...',
        nextAction: 'generating'
      }));
      
      const response = await candidateAPI.generatePrescreeningQuestions();
      setQuestions(response.data);
      
      // Reload session
      const sessionResponse = await candidateAPI.getPrescreeningSession();
      setSession(sessionResponse.data);
      
      // Update state to show questions are ready
      setPrescreeningState({
        isAvailable: true,
        isEnabled: true,
        isLoading: false,
        hasResume: true,
        isGeneratingQuestions: false,
        statusMessage: `${response.data.length} questions ready - Start your assessment`,
        nextAction: 'start_assessment'
      });
      
      toast.success('Prescreening questions generated successfully!');
    } catch (error) {
      console.error('Error generating questions:', error);
      
      // Reset to previous state on error
      setPrescreeningState(prev => ({
        ...prev,
        isGeneratingQuestions: false,
        statusMessage: 'Failed to generate questions. Please try again.',
        nextAction: 'retry_generate'
      }));
      
      toast.error('Failed to generate prescreening questions');
    } finally {
      setIsGenerating(false);
    }
  };


  const handleAnswerChange = (questionId, value, type) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        [type]: value,
        timeTakenSeconds: prev[questionId]?.timeTakenSeconds || 0
      }
    }));
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const submitAnswers = async () => {
    try {
      setIsLoading(true);
      
      // Convert answers to API format
      const answerRequests = questions.map(question => {
        const answer = answers[question.id] || {};
        return {
          questionId: question.id,
          answerText: answer.answerText || null,
          selectedOption: answer.selectedOption || null,
          ratingValue: answer.ratingValue || null,
          isYes: answer.isYes || null,
          timeTakenSeconds: answer.timeTakenSeconds || 0,
          isSkipped: !answer.answerText && !answer.selectedOption && answer.isYes === null && answer.ratingValue === null
        };
      });
      
      await candidateAPI.submitPrescreeningAnswers(answerRequests);
      
      // Complete the session
      const completedSession = await candidateAPI.completePrescreeningSession();
      setSession(completedSession.data);
      setShowResults(true);
      
      toast.success('Prescreening completed successfully!');
      
      // Notify parent component
      if (onComplete) {
        onComplete();
      }
    } catch (error) {
      console.error('Error submitting answers:', error);
      toast.error('Failed to submit answers');
    } finally {
      setIsLoading(false);
    }
  };

  const renderQuestion = (question) => {
    const answer = answers[question.id] || {};

    switch (question.questionType) {
      case 'multiple_choice':
        return (
          <div className="space-y-3">
            {question.options.map((option, index) => (
              <label key={index} className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name={`question-${question.id}`}
                  value={option}
                  checked={answer.selectedOption === option}
                  onChange={(e) => handleAnswerChange(question.id, e.target.value, 'selectedOption')}
                  className="w-4 h-4 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-gray-700">{option}</span>
              </label>
            ))}
          </div>
        );

      case 'yes_no':
        return (
          <div className="flex space-x-6">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="radio"
                name={`question-${question.id}`}
                checked={answer.isYes === true}
                onChange={() => handleAnswerChange(question.id, true, 'isYes')}
                className="w-4 h-4 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-gray-700">Yes</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="radio"
                name={`question-${question.id}`}
                checked={answer.isYes === false}
                onChange={() => handleAnswerChange(question.id, false, 'isYes')}
                className="w-4 h-4 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-gray-700">No</span>
            </label>
          </div>
        );

      case 'rating':
        return (
          <div className="space-y-2">
            <div className="flex space-x-2">
              {[1, 2, 3, 4, 5].map((rating) => (
                <label key={rating} className="flex items-center space-x-1 cursor-pointer">
                  <input
                    type="radio"
                    name={`question-${question.id}`}
                    value={rating}
                    checked={answer.ratingValue === rating}
                    onChange={(e) => handleAnswerChange(question.id, parseInt(e.target.value), 'ratingValue')}
                    className="w-4 h-4 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-gray-700">{rating}</span>
                </label>
              ))}
            </div>
            <div className="flex justify-between text-xs text-gray-500">
              <span>Poor</span>
              <span>Excellent</span>
            </div>
          </div>
        );

      case 'text':
      default:
        return (
          <textarea
            value={answer.answerText || ''}
            onChange={(e) => handleAnswerChange(question.id, e.target.value, 'answerText')}
            rows="4"
            placeholder="Enter your answer here..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
          />
        );
    }
  };

  // Show loading state
  if (isLoading || prescreeningState.isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <span className="ml-2 text-gray-600">Loading prescreening data...</span>
      </div>
    );
  }

  // Show results if session is completed
  if (showResults && session) {
    return (
      <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-lg">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center bg-green-100">
            <span className="text-2xl text-green-600">
              ✓
            </span>
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Prescreening Completed
          </h2>
          
          <div className="mt-6 p-6 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-green-800">Assessment Submitted Successfully</h3>
                <p className="text-sm text-green-600 mt-1">
                  Thank you for completing the prescreening assessment. Your responses have been submitted and will be reviewed by our team.
                </p>
              </div>
            </div>
          </div>
          
          <div className="mt-6 text-sm text-gray-600">
            <p>You answered {session.answeredQuestions} out of {session.totalQuestions} questions.</p>
            <p className="mt-2 text-gray-500">
              We'll review your responses and get back to you with next steps in the hiring process.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show conditional prescreening UI based on state
  if (!prescreeningState.isAvailable || prescreeningState.nextAction === 'upload_resume' || prescreeningState.nextAction === 'generate_questions' || prescreeningState.nextAction === 'generating') {
    return (
      <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-lg">
        <div className="text-center">
          <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
            prescreeningState.isEnabled ? 'bg-primary-100' : 'bg-gray-100'
          }`}>
            <span className={`text-2xl ${
              prescreeningState.isEnabled ? 'text-primary-600' : 'text-gray-400'
            }`}>
              {prescreeningState.nextAction === 'generating' ? '⏳' : 
               prescreeningState.nextAction === 'upload_resume' ? '📝' : 
               prescreeningState.nextAction === 'generate_questions' ? '✅' : '❓'}
            </span>
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Prescreening Assessment</h2>
          
          <p className={`text-lg mb-6 ${
            prescreeningState.isEnabled ? 'text-gray-700' : 'text-gray-500'
          }`}>
            {prescreeningState.statusMessage}
          </p>
          
          <div className={`transition-all duration-300 ${
            prescreeningState.isEnabled ? 'opacity-100' : 'opacity-60'
          }`}>
            {prescreeningState.nextAction === 'upload_resume' && (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Upload your resume to automatically generate personalized prescreening questions based on your skills and experience.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    💡 <strong>Tip:</strong> Go to your profile to upload your resume first.
                  </p>
                </div>
              </div>
            )}
            
            {prescreeningState.nextAction === 'generate_questions' && (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Your resume has been uploaded! Generate personalized prescreening questions to showcase your skills.
                </p>
                <button
                  onClick={generateQuestions}
                  disabled={isGenerating || prescreeningState.isGeneratingQuestions}
                  className="bg-primary-600 text-white px-8 py-3 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isGenerating || prescreeningState.isGeneratingQuestions ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Generating Questions...
                    </>
                  ) : (
                    'Generate Questions'
                  )}
                </button>
              </div>
            )}
            
            {prescreeningState.nextAction === 'generating' && (
              <div className="space-y-4">
                <div className="flex items-center justify-center">
                  <svg className="animate-spin h-8 w-8 text-primary-600" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
                <p className="text-sm text-gray-600">
                  Creating personalized questions based on your resume...
                </p>
              </div>
            )}

            {prescreeningState.nextAction === 'retry' && (
              <div className="space-y-4">
                <button
                  onClick={loadPrescreeningData}
                  className="bg-primary-600 text-white px-8 py-3 rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Try again
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Show questions if session is in progress
  if (questions.length === 0) {
    return (
      <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-lg text-center">
        <p className="text-gray-600">No questions available. Please generate questions first.</p>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;
  const categoryLabel = formatPrescreeningCategory(currentQuestion.category);

  return (
    <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex justify-between text-sm text-gray-600 mb-2">
          <span>Question {currentQuestionIndex + 1} of {questions.length}</span>
          <span>{Math.round(progress)}% Complete</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-primary-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      </div>

      {/* Question */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          {categoryLabel ? (
            <span className="inline-block text-xs font-medium text-primary-800 bg-primary-50 px-2.5 py-1 rounded-full">
              {categoryLabel}
            </span>
          ) : (
            <span />
          )}
          {currentQuestion.estimatedTimeMinutes ? (
            <span className="text-sm text-gray-500">
              About {currentQuestion.estimatedTimeMinutes} min
            </span>
          ) : (
            <span />
          )}
        </div>
        
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {currentQuestion.questionText}
        </h3>
        
        {renderQuestion(currentQuestion)}
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={handlePrevious}
          disabled={currentQuestionIndex === 0}
          className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        
        {currentQuestionIndex === questions.length - 1 ? (
          <button
            onClick={submitAnswers}
            disabled={isLoading}
            className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Submitting...' : 'Submit Answers'}
          </button>
        ) : (
          <button
            onClick={handleNext}
            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700"
          >
            Next
          </button>
        )}
      </div>
    </div>
  );
};

export default PrescreeningQuestions;
